import { Injectable, inject } from "@angular/core";
import { ProcessInterfacesResource } from "core/stages-client";
import { BehaviorSubject } from "rxjs";
import { Selectable } from "common/selection/state/selectable.model";

type ManualInterfaceSelection = stages.process.release.ManualInterfaceSelection;
type ProcessInterfaceWorkspaceSelectionOverview =
	stages.management.interfaces.ProcessInterfaceWorkspaceSelectionOverview;
type ProcessElementInterface = stages.management.interfaces.ProcessElementInterface;
type ProcessInterfaceWorkspace = stages.management.interfaces.ProcessInterfaceWorkspace;
type ReleasePreviewSetting = stages.process.subcomponent.interfaces.core.ReleasePreviewSetting;

@Injectable({ providedIn: "root" })
export class ProcessInterfacesService {
	private readonly processInterfacesResource = inject(ProcessInterfacesResource);

	_filterableElementTypes: string[] = [];

	targetWorkspaceId$ = new BehaviorSubject<string | undefined>(undefined);
	workspaceNameFrom = new BehaviorSubject<string | undefined>(undefined);
	workspaceNameTo = new BehaviorSubject<string | undefined>(undefined);
	showReleaseWarning$ = new BehaviorSubject<boolean>(false);
	workspacePage$ = new BehaviorSubject<number>(1);
	filterableElementTypes$ = new BehaviorSubject<string[]>([]);
	filterElementType$ = new BehaviorSubject<string | undefined>(undefined);

	setTargetWorkspaceId(targetWorkspaceId: string | undefined): void {
		this.targetWorkspaceId$.next(targetWorkspaceId);
	}

	setWorkspacePage(workspacePage: number): void {
		this.workspacePage$.next(workspacePage);
	}

	async loadMoreWorkspaces(
		page: number,
		pageSize: number,
		showOnlyChanges: boolean,
		releasePreviewSetting: ReleasePreviewSetting,
		workspaceId: string,
		processVersion: string,
		processVersionToCompare: string,
		withWorkflowPolicy: boolean,
	): Promise<SecuredPagedResultAndInfo<ProcessInterfaceWorkspace, ProcessInterfaceWorkspaceSelectionOverview[]>> {
		return this.processInterfacesResource.getInterfaceWorkspaces(workspaceId, {
			page: page,
			pageSize: pageSize,
			showOnlyChanges: showOnlyChanges,
			releasePreviewSetting: releasePreviewSetting,
			processVersion: processVersion,
			processVersionToCompareWith: processVersionToCompare,
			withWorkflowPolicy: withWorkflowPolicy,
		});
	}

	async loadMoreInterfaces(
		page: number,
		pageSize: number,
		workspaceId: string,
		targetWorkspaceId: string,
		processVersion: string,
		processVersionToCompare: string,
		showOnlyChanges: boolean,
		releasePreviewSetting: ReleasePreviewSetting,
		filterType: string,
		filterTerm: string,
	): Promise<PagedResult<ProcessElementInterface>> {
		const interfacesWithDetails = await this.processInterfacesResource.getProcessInterfaces(
			workspaceId,
			targetWorkspaceId,
			{
				page: page,
				pageSize: pageSize,
				processVersion: processVersion,
				processVersionToCompareWith: processVersionToCompare,
				showOnlyChanges: showOnlyChanges,
				releasePreviewSetting: releasePreviewSetting,
				filterType: filterType,
				filterTerm: filterTerm,
			},
		);

		if (
			interfacesWithDetails.filterableElementTypes.length !== this._filterableElementTypes.length ||
			!interfacesWithDetails.filterableElementTypes.every((value, index) => {
				return value === this._filterableElementTypes[index];
			})
		) {
			this._filterableElementTypes = interfacesWithDetails.filterableElementTypes;
			this.filterableElementTypes$.next(interfacesWithDetails.filterableElementTypes);
		}

		if (interfacesWithDetails.fromWorkspace) {
			this.workspaceNameFrom.next(interfacesWithDetails.fromWorkspace);
		}

		if (interfacesWithDetails.toWorkspace) {
			this.workspaceNameTo.next(interfacesWithDetails.toWorkspace);
		}

		return interfacesWithDetails.processInterfaces;
	}
}

// TODO simplify
export function splitBy(
	selectedOrIndeterminateWorkspaces: Selectable[],
	interfacesToReleaseSelectables: Selectable[],
): ManualInterfaceSelection[] {
	const selectedInterfaces = interfacesToReleaseSelectables.reduce(
		(interfacesToRelease: ManualInterfaceSelection[], selectable: Selectable) => {
			const existingInterfaceWorkspace = interfacesToRelease.find(
				(interfaceToRelease) => selectable.parentId === interfaceToRelease.workspaceId,
			);

			if (existingInterfaceWorkspace) {
				if (!selectable.initiallySelected && selectable.isSelected) {
					existingInterfaceWorkspace.manuallySelectedAssociationIds.push(selectable.id);
				}
				if (selectable.initiallySelected && !selectable.isSelected) {
					existingInterfaceWorkspace.manuallyDeselectedAssociationIds.push(selectable.id);
				}
			} else if (
				selectable.isSelected &&
				selectedOrIndeterminateWorkspaces.find((w) => w.id === selectable.parentId!)
			) {
				// add to added interfaces list
				interfacesToRelease.push({
					workspaceId: selectable.parentId!,
					manuallySelectedAssociationIds: [selectable.id],
					manuallyDeselectedAssociationIds: [],
					onlyPreselected: false,
				});
			} else if (
				!selectable.isSelected &&
				selectedOrIndeterminateWorkspaces.find((w) => w.id === selectable.parentId!)
			) {
				// add to removed interfaces list
				interfacesToRelease.push({
					workspaceId: selectable.parentId!,
					manuallySelectedAssociationIds: [],
					manuallyDeselectedAssociationIds: [selectable.id],
					onlyPreselected: false,
				});
			}
			return interfacesToRelease;
		},
		[],
	);

	const allSelectedWorkspaces: ManualInterfaceSelection[] = [];

	selectedOrIndeterminateWorkspaces.forEach((selectedWorkspace: Selectable) => {
		if (
			(selectedWorkspace.isSelected || selectedWorkspace.indeterminate) &&
			!selectedInterfaces.find((s) => selectedWorkspace.id === s.workspaceId)
		) {
			allSelectedWorkspaces.push({
				workspaceId: selectedWorkspace.id,
				manuallySelectedAssociationIds: [],
				manuallyDeselectedAssociationIds: [],
				onlyPreselected: !!selectedWorkspace.indeterminate,
			});
		}
	});

	return [...allSelectedWorkspaces, ...selectedInterfaces];
}
